import React from "react"
import { useModalProvider } from "../../../../../../Providers/ModalProvider"
import { getColors, MobergAnimationCurve, MobergAnimationTiming, MobergColumn, MobergFontSize, MobergHeader, MobergIconSize, MobergRow, MobergText, MobergThemeColors } from "../../../../../../Moberg"
import { MdCircle, MdNotificationsOff } from "react-icons/md"
import { Scrollbar } from "../../../../../../Constants/StyledComponents"
import styled from "styled-components"
import { useAlertsProvider } from "../../../../../../Providers/AlertsProvider"
import MobergToggle from "../../../../../../Components/MobergToggle/MobergToggle"
import ReactMarkdown from "react-markdown"
import { useRecoilValue } from "recoil"
import { currentPatientFileInfoAtom } from "../../Atoms/PatientFile"
import { localizeDate } from "../../../../../../Computation/utilFunctions"
import { useAuthProvider } from "../../../../../../Providers/AuthProvider"
import { ModalTitleAndCloseButton } from "../Modals/ModalTitleAndCloseButton"

const AlertDetailedView: React.FC = () => {
    const { selectedAlert, getAlertTheme, getAlertIcon, toggleSilenced } = useAlertsProvider()
    const patientFileInfo = useRecoilValue(currentPatientFileInfoAtom)
    const authProvider = useAuthProvider()
    const canManageAlerts = authProvider.permissions?.['technical_alert:manage']

    if (!selectedAlert) {
        return null
    }

    const theme = getAlertTheme(selectedAlert)
    const mainColor = getColors(theme).main

    return (
        <Scrollbar style={{ overflowY: "auto", flex: 1 }}>
            <MobergColumn gap="20px" style={{ padding: "20px" }}>
                <MobergColumn horizontalAlign="center" gap="8px" style={{ textAlign: "center", padding: "40px 16px", borderRadius: "4px", background: mainColor, color: "white" }}>
                    {getAlertIcon(selectedAlert)}
                    <MobergHeader style={{ fontSize: MobergFontSize.XX_LARGE, color: "white" }}>{selectedAlert.title}</MobergHeader>
                    <div style={{ whiteSpace: "pre-line", opacity: 0.9 }}>
                        <ReactMarkdown>{selectedAlert.description}</ReactMarkdown>
                    </div>
                </MobergColumn>

                <MobergRow gap="8px" verticalAlign="center">
                    <MdNotificationsOff size={MobergIconSize.REGULAR} color="var(--text-secondary-color)" />
                    <MobergHeader style={{ color: "var(--text-secondary-color)" }}>Silenced</MobergHeader>
                    <MobergToggle
                        key={selectedAlert.id}
                        theme={theme}
                        checked={selectedAlert.silenced}
                        onChange={() => toggleSilenced(selectedAlert.id)}
                        disabled={!canManageAlerts} //TODO: Add tooltip to the component??
                    />
                </MobergRow>

                <MobergColumn gap="4px" style={{ paddingLeft: "8px", borderLeft: `4px solid ${mainColor}` }}>
                    <MobergHeader>Time</MobergHeader>
                    <MobergText>{localizeDate(new Date(selectedAlert.timestamp), patientFileInfo?.timeZone)}</MobergText>
                </MobergColumn>

                <MobergColumn gap="4px" style={{ paddingLeft: "8px", borderLeft: `4px solid ${mainColor}` }}>
                    <MobergHeader>Steps to resolve</MobergHeader>
                    {typeof selectedAlert.resolutions === "string" ? (
                        <ReactMarkdown
                            components={{
                                a: ({ children, ...props }) => (
                                    <a
                                        {...props}
                                        style={{
                                            color: "var(--color-accent-interactive)",
                                            textDecoration: "underline",
                                            cursor: "pointer"
                                        }}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                    >
                                        {children}
                                    </a>
                                )
                            }}
                        >
                            {selectedAlert.resolutions}
                        </ReactMarkdown>
                    ) : selectedAlert.resolutions}
                </MobergColumn>
            </MobergColumn>
        </Scrollbar>
    )
}

export const AlertsList: React.FC = () => {
    const { selectedAlert, alerts, getAlertTheme, setSelectedAlertId } = useAlertsProvider()
    const { close } = useModalProvider()
    const patientFileInfo = useRecoilValue(currentPatientFileInfoAtom)

    if (alerts.length === 0) {
        return (
            <ModalContainer style={{ width: "500px", height: "300px" }}>
                <ModalTitleAndCloseButton title="Alerts" closeFunction={close} />
                <hr style={{ border: "1px solid var(--color-border-default)", margin: 0 }} />
                <MobergColumn expand={true} verticalAlign="center" horizontalAlign="center">
                    <MobergHeader>No alerts to display</MobergHeader>
                </MobergColumn>
            </ModalContainer>
        )
    }

    const sortedAlerts = [...alerts].sort((a, b) => a.timestamp < b.timestamp ? 1 : -1)

    return (
        <ModalContainer>
            <ModalTitleAndCloseButton title="Alerts" closeFunction={close} />
            <hr style={{ border: "1px solid var(--color-border-default)", margin: 0 }} />

            <div style={{ display: "flex", flex: 1, minHeight: 0 }}>
                <Scrollbar style={{ overflowY: "auto", width: "280px", flexShrink: 0, borderRight: "1px solid var(--color-border-default)" }}>
                    {sortedAlerts.map(alert => {
                        const theme = getAlertTheme(alert)
                        const colors = getColors(theme)

                        return (
                            <AlertListItem
                                key={alert.id}
                                colors={colors}
                                active={alert.id === selectedAlert?.id}
                                onMouseDown={() => setSelectedAlertId(alert.id)}
                            >
                                <MobergRow gap="8px" verticalAlign="start" style={{ padding: "12px 16px" }}>
                                    <span style={{ marginTop: "4px" }}>
                                        <MdCircle size={MobergIconSize.X_SMALL} color={alert.silenced ? "transparent" : colors.main} />
                                    </span>
                                    <MobergColumn gap="2px">
                                        <MobergHeader style={{ fontSize: MobergFontSize.REGULAR }}>{alert.title}</MobergHeader>
                                        <MobergText style={{ fontSize: MobergFontSize.X_SMALL, color: "var(--text-secondary-color)" }}>
                                            {localizeDate(new Date(alert.timestamp), patientFileInfo?.timeZone)}
                                        </MobergText>
                                    </MobergColumn>
                                </MobergRow>
                            </AlertListItem>
                        )
                    })}
                </Scrollbar>

                <MobergColumn style={{ flex: 1, minHeight: 0 }}>
                    <AlertDetailedView />
                </MobergColumn>
            </div>
        </ModalContainer>
    )
}

const ModalContainer = styled.div`
    display: flex;
    flex-direction: column;
    width: 800px;
    height: 70vh;
    max-height: 600px;
    background: var(--color-surface-panel);
    color: var(--color-text-primary);
`

type AlertListItemProps = {
    colors: MobergThemeColors
    active: boolean
}

const AlertListItem = styled.div<AlertListItemProps>`
    transition: all ${MobergAnimationTiming.FAST} ${MobergAnimationCurve.EASE_IN};
    background: ${({ active }) => active ? "var(--color-hover-active-neutral)" : "transparent"};
    border-bottom: 1px solid var(--color-border-light);
    cursor: pointer;

    &:hover {
        background: ${({ active }) => active ? "var(--color-hover-active-neutral)" : "var(--color-hover-neutral)"};
    }

    &:active {
        background: var(--color-hover-active-neutral);
    }
`
